'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { IconCheck, IconLoader } from '@tabler/icons-react'
import { PRICING_TIERS } from '@/lib/landing-constants'
import { createCheckoutSession, createFreeSubscription } from '@/lib/api-client'
import { useToast } from '@/components/ui/toast'

interface SubscriptionModalProps {
  onComplete: () => void
  onClose: () => void
  isPolling?: boolean // Shows that system is actively checking for subscription
}

export function SubscriptionModal({ onComplete, onClose, isPolling = false }: SubscriptionModalProps) {
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')
  const [isLoading, setIsLoading] = useState(false)
  const { addToast } = useToast()

  // Filter out Enterprise - only show Starter and Professional
  const availablePlans = PRICING_TIERS.filter(tier => tier.name !== 'Enterprise')

  const handleSubscribe = async (tierName: string) => {
    setIsLoading(true)
    try {
      // Map tier name to plan ID from backend
      const planMap: Record<string, string> = {
        'Starter': '5adbfecc-8e5d-4bbe-a6ab-2b5af413794b',
        'Professional': '89b05c92-3ecf-4c83-8f86-700a3fd11ba7',
      }

      const planId = planMap[tierName]
      if (!planId) {
        throw new Error('Invalid plan selected')
      }

      // Find the tier to check if it's free
      const tier = PRICING_TIERS.find(t => t.name === tierName)
      const price = billingInterval === 'yearly' && tier?.annualPrice !== undefined
        ? tier.annualPrice
        : tier?.monthlyPrice ?? 0

      // Check if this is a free plan
      if (price === 0) {
        // Free plan - create subscription directly without Stripe
        await createFreeSubscription({
          plan_id: planId,
          billing_cycle: billingInterval,
        })

        addToast({
          title: 'Success',
          message: 'Your free subscription has been activated!',
          variant: 'success',
        })

        // Call onComplete to refresh the subscription state
        onComplete()
      } else {
        // Paid plan - redirect to Stripe checkout
        const result = await createCheckoutSession({
          plan_id: planId,
          billing_cycle: billingInterval,
          success_url: `${window.location.origin}/dashboard?subscription_complete=true`,
          cancel_url: `${window.location.origin}/dashboard?subscription_canceled=true`,
        })

        if (result.checkout_url) {
          // User will be redirected to Stripe - page will reload when they return
          window.location.href = result.checkout_url
          return // Don't clear loading state since page will reload
        }
      }
    } catch (error: any) {
      addToast({
        title: 'Error',
        message: error.message || 'Failed to start checkout',
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl p-8 m-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 !text-black">Choose Your Plan</h1>
          <p className="text-gray-600 text-lg">
            Start your gamification journey today. All plans require a subscription.
          </p>
          {/* Polling indicator - shows when checking for subscription in database */}
          {isPolling && (
            <div className="mt-4 inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm">
              <IconLoader className="h-4 w-4 animate-spin" />
              <span>Waiting for subscription confirmation...</span>
            </div>
          )}
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className={billingInterval === 'monthly' ? '!text-black font-semibold' : '!text-black text-gray-600'}>
            Monthly
          </span>
          <button
            onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600 transition-colors focus:outline-none"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              billingInterval === 'yearly' ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
          <span className={billingInterval === 'yearly' ? '!text-black font-semibold' : '!text-black text-gray-600'}>
            Yearly <span className="text-green-600 text-sm">(Save 20%)</span>
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          {availablePlans.map((tier) => {
            const price = billingInterval === 'yearly' && tier.annualPrice
              ? tier.annualPrice
              : tier.monthlyPrice

            const isPopular = tier.popular

            return (
              <Card
                key={tier.name}
                className={`relative ${isPopular ? 'border-blue-600 shadow-lg' : ''}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600">Most Popular</Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-2xl">{tier.name}</CardTitle>
                  <CardDescription>{tier.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">
                      {price === 0 ? 'Free' : `$${price}`}
                    </span>
                    <span className="text-gray-600">/month</span>
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <IconCheck className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="w-full"
                    variant={isPopular ? 'default' : 'outline'}
                    onClick={() => handleSubscribe(tier.name)}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : tier.cta}
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Trust Badges */}
        <div className="text-center text-sm text-gray-600">
          <p>Secure payment powered by Stripe. Cancel anytime.</p>
          <p className="mt-1">By subscribing, you agree to our Terms of Service.</p>
        </div>
      </div>
    </div>
  )
}
